import LivingMap, {
  LivingMapPlugin,
  LayerDelegate,
} from "@livingmap/core-mapping";
import { MapMouseEvent } from "mapbox-gl";
import { Geofence, Region } from "../../../redux/services/types";
import { Feature, FeatureCollection } from "geojson";
import centroid from "@turf/centroid";

export default class DebugControl extends LivingMapPlugin {
  protected layerDelegate: LayerDelegate;

  constructor(id: string, LMMap: LivingMap) {
    super(id, LMMap);
    this.LMMap = LMMap;
    this.layerDelegate = this.LMMap.getLayerDelegate();
  }

  activate(): void {}

  deactivate(): void {}

  refresh(): void {
    return;
  }

  public enableDebug() {
    this.LMMap.getMapboxMap().on("click", this.logMapCoordinate);
    this.addMapboxDebugVisuals(true);
  }

  public disableDebug() {
    this.LMMap.getMapboxMap().off("click", this.logMapCoordinate);
    this.addMapboxDebugVisuals(false);
  }

  /**
   * Function will add some basic mapbox debug visuals on top of the map. It will:
   * - Visualise collionBoxes on Map elements
   * - Visualise Tile boundaries
   * @private
   */
  private addMapboxDebugVisuals(state: boolean): void {
    const mapInstance = this.LMMap.getMapboxMap();
    mapInstance.showCollisionBoxes = state;
    mapInstance.showTileBoundaries = state;
  }

  /**
   * Given a click on the map, this function will log the clicked coordinates to the browser console
   * @private
   * @param e
   */
  private logMapCoordinate(event: MapMouseEvent): void {
    const lngLat = event.lngLat;
    const positionData = {
      coordinates: {
        latitude: lngLat.lat,
        longitude: lngLat.lng,
      },
      formatted: {
        latLng: `${lngLat.lat}, ${lngLat.lng}`,
        lngLat: `${lngLat.lng}, ${lngLat.lat}`,
        mysql: `POINT(${lngLat.lng} ${lngLat.lat})`,
        postgresql: `SRID=4326;POINT(${lngLat.lng} ${lngLat.lat})`,
      },
    };
    console.log(positionData);
  }

  public setGeofences(geofences: Geofence[]) {
    const polygons = geofences.map((geofence) => {
      return {
        type: "Feature",
        geometry: geofence.geometry,
        properties: {
          floor_id: geofence.floor_id,
          name: geofence.name,
        },
      };
    }) as Feature[];
    const featureCollection = this.generateFeatureCollection(polygons);
    this.createDebugPolygonLayer(
      featureCollection,
      "geofences",
      "rgba(91, 148, 198, 0.3)",
    );
  }

  public setRegions(regions: Region[]) {
    const polygons = regions.map((region) => {
      return {
        type: "Feature",
        geometry: region.geometry,
        properties: {
          name: region.name,
        },
      };
    }) as Feature[];
    const featureCollection = this.generateFeatureCollection(polygons);
    this.createDebugPolygonLayer(
      featureCollection,
      "regions",
      "rgba(198, 91, 91, 0.3)",
    );
  }

  private createDebugPolygonLayer(
    geoJsonFeatures: FeatureCollection,
    typeName: string,
    color: string,
  ) {
    const sourceName = typeName + "_source";
    const layerName = typeName + "_layer";
    const layerBorder = typeName + "_layer_border";

    const labelPoints = this.generateLabelsFromPolygons(geoJsonFeatures);
    const sourceLabelName = typeName + "_label_source";
    const layerLabelName = typeName + "_label_layer";

    try {
      this.layerDelegate.addSource(sourceName, {
        type: "geojson",
        data: geoJsonFeatures,
      });
    } catch (error) {} // already exists ignore

    try {
      this.layerDelegate.addLayer({
        id: layerName,
        type: "fill",
        source: sourceName,
        paint: {
          "fill-color": color,
        },
      });
      this.layerDelegate.addLayer({
        id: layerBorder,
        type: "line",
        source: sourceName,
        layout: {},
        paint: {
          "line-color": "#000",
          "line-width": 1,
        },
      });
    } catch (error) {} // already exists ignore
    try {
      this.layerDelegate.addSource(sourceLabelName, {
        type: "geojson",
        data: labelPoints,
      });
    } catch (error) {} // already exists ignore
    try {
      this.layerDelegate.addLayer({
        id: layerLabelName,
        type: "symbol",
        source: sourceLabelName,
        layout: {
          "text-field": ["get", "name"],
          "text-font": ["IBMPlexSans-SemiBold"],
          "text-size": 10,
          "text-line-height": 1.2,
          "text-letter-spacing": 0.025,
          "text-transform": "none",
          "text-max-width": 7,
          "text-padding": 22,
        },
        paint: {
          "text-color": "rgba(0, 39, 63, 1)",
          "text-halo-color": "rgba(255, 255, 255, 0.65)",
          "text-halo-width": 1,
        },
      });
    } catch (error) {} // already exists ignore
  }

  private generateLabelsFromPolygons(
    features: FeatureCollection,
  ): FeatureCollection {
    const points = features.features.map((feature) => {
      const point = centroid(feature);
      point.properties = feature.properties;
      return point;
    });
    return this.generateFeatureCollection(points);
  }

  private generateFeatureCollection(features: Feature[]): FeatureCollection {
    return {
      type: "FeatureCollection",
      features: features,
    };
  }
}
